"use client"

import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as DialogPrimitive } from "radix-ui"

import { cn } from "@/custolink-newdesign/lib/utils"
import { Button } from "@/custolink-newdesign/ui/button"

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "cl:fixed cl:inset-0 cl:z-50 cl:bg-black/50 cl:data-[state=closed]:animate-out cl:data-[state=closed]:fade-out-0 cl:data-[state=open]:animate-in cl:data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      {/* grid-cols-[minmax(0,1fr)]: the registry's implicit auto column grows to
          its widest child's min-content, so one long unbreakable line (a file
          name) pushed the content out past the rounded box. */}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "cl:fixed cl:top-[50%] cl:left-[50%] cl:z-50 cl:grid cl:grid-cols-[minmax(0,1fr)] cl:w-full cl:max-w-[calc(100%-2rem)] cl:translate-x-[-50%] cl:translate-y-[-50%] cl:gap-4 cl:rounded-lg cl:border cl:bg-background cl:p-6 cl:shadow-lg cl:duration-200 cl:outline-none cl:data-[state=closed]:animate-out cl:data-[state=closed]:fade-out-0 cl:data-[state=closed]:zoom-out-95 cl:data-[state=open]:animate-in cl:data-[state=open]:fade-in-0 cl:data-[state=open]:zoom-in-95 cl:sm:max-w-lg",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="cl:absolute cl:top-4 cl:right-4 cl:rounded-xs cl:opacity-70 cl:ring-offset-background cl:transition-opacity cl:hover:opacity-100 cl:focus:ring-2 cl:focus:ring-ring cl:focus:ring-offset-2 cl:focus:outline-hidden cl:disabled:pointer-events-none cl:data-[state=open]:bg-accent cl:data-[state=open]:text-muted-foreground cl:[&_svg]:pointer-events-none cl:[&_svg]:shrink-0 cl:[&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="cl:sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("cl:flex cl:flex-col cl:gap-2 cl:text-center cl:sm:text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "cl:flex cl:flex-col-reverse cl:gap-2 cl:sm:flex-row cl:sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("cl:text-lg cl:leading-none cl:font-semibold", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("cl:text-sm cl:text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
